import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Box, CircularProgress, Typography } from '@mui/material';
import { loadPdfDocument } from '../../utils/pdfjsWorker';

const PAGE_GAP = 8;
const PREVIEW_INSET = 8;
const WIDTH_EPSILON = 4;
/** A4 portrait height / width */
const A4_HEIGHT_RATIO = 297 / 210;
const FALLBACK_MIN_HEIGHT = 520;

function clearMountNode(node) {
  if (!node) return;
  while (node.firstChild) {
    node.removeChild(node.firstChild);
  }
}

function getAvailableWidth(shellNode) {
  return Math.max(shellNode.clientWidth - PREVIEW_INSET * 2, 120);
}

function estimateDefaultPreviewHeight(shellNode) {
  if (!shellNode) return FALLBACK_MIN_HEIGHT;
  return Math.ceil(getAvailableWidth(shellNode) * A4_HEIGHT_RATIO) + PREVIEW_INSET * 2;
}

function createWrapper() {
  const wrapper = document.createElement('div');
  wrapper.style.width = '100%';
  wrapper.style.boxSizing = 'border-box';
  wrapper.style.padding = `${PREVIEW_INSET}px`;
  wrapper.style.display = 'flex';
  wrapper.style.flexDirection = 'column';
  wrapper.style.alignItems = 'center';
  return wrapper;
}

async function renderPageToCanvas(page, availableWidth, maxWidth, pixelRatio) {
  const scale = (availableWidth / maxWidth) * pixelRatio;
  const scaledViewport = page.getViewport({ scale });
  const canvas = document.createElement('canvas');
  canvas.width = Math.floor(scaledViewport.width);
  canvas.height = Math.floor(scaledViewport.height);
  canvas.style.display = 'block';
  canvas.style.width = `${Math.floor(scaledViewport.width / pixelRatio)}px`;
  canvas.style.height = `${Math.floor(scaledViewport.height / pixelRatio)}px`;
  canvas.style.flexShrink = '0';
  canvas.style.boxShadow = '0 12px 32px rgba(15, 23, 42, 0.18)';
  canvas.style.borderRadius = '4px';
  await page.render({
    canvasContext: canvas.getContext('2d'),
    viewport: scaledViewport,
  }).promise;
  return canvas;
}

function mountWrapper(mountNode, wrapper) {
  const existing = mountNode.firstChild;
  if (existing) {
    mountNode.replaceChild(wrapper, existing);
  } else {
    mountNode.appendChild(wrapper);
  }
}

export default function PdfFitPreview({
  pdfUrl,
  accentColor = '#4ce1b6',
  minHeight: minHeightProp,
  onReady,
  onError,
}) {
  const shellRef = useRef(null);
  const mountRef = useRef(null);
  const renderGenerationRef = useRef(0);
  const lastWidthRef = useRef(0);
  const hasContentRef = useRef(false);
  const contentHeightRef = useRef(0);
  const onReadyRef = useRef(onReady);
  const onErrorRef = useRef(onError);
  const [initialLoading, setInitialLoading] = useState(Boolean(pdfUrl));
  const [error, setError] = useState(null);
  const [reservedHeight, setReservedHeight] = useState(minHeightProp || FALLBACK_MIN_HEIGHT);

  onReadyRef.current = onReady;
  onErrorRef.current = onError;

  const syncReservedHeight = useCallback(() => {
    const shellNode = shellRef.current;
    if (!shellNode) return;
    const a4Height = estimateDefaultPreviewHeight(shellNode);
    const next = Math.max(
      minHeightProp || 0,
      a4Height,
      contentHeightRef.current || 0,
    );
    setReservedHeight(next);
  }, [minHeightProp]);

  useEffect(() => {
    syncReservedHeight();
    const shellNode = shellRef.current;
    if (!shellNode) return undefined;

    const observer = new ResizeObserver(() => {
      syncReservedHeight();
    });
    observer.observe(shellNode);
    return () => observer.disconnect();
  }, [syncReservedHeight]);

  useEffect(() => {
    if (!pdfUrl) {
      hasContentRef.current = false;
      lastWidthRef.current = 0;
      contentHeightRef.current = 0;
      setInitialLoading(false);
      setError(null);
      if (mountRef.current) clearMountNode(mountRef.current);
      syncReservedHeight();
      return undefined;
    }

    if (!mountRef.current || !shellRef.current) return undefined;

    let cancelled = false;
    const mountNode = mountRef.current;
    const shellNode = shellRef.current;
    const generation = renderGenerationRef.current + 1;
    renderGenerationRef.current = generation;

    syncReservedHeight();

    async function renderPdf() {
      const isFirstLoad = !hasContentRef.current;
      if (isFirstLoad) {
        setInitialLoading(true);
      }

      try {
        const availableWidth = getAvailableWidth(shellNode);
        const pdf = await loadPdfDocument(pdfUrl);
        if (cancelled || generation !== renderGenerationRef.current) return;

        const numPages = pdf.numPages;
        const firstPage = await pdf.getPage(1);
        const firstViewport = firstPage.getViewport({ scale: 1 });
        let maxWidth = firstViewport.width;

        // Probe remaining page widths without rendering yet (cheap).
        for (let pageNum = 2; pageNum <= numPages; pageNum += 1) {
          const page = await pdf.getPage(pageNum);
          maxWidth = Math.max(maxWidth, page.getViewport({ scale: 1 }).width);
        }

        const pixelRatio = window.devicePixelRatio || 1;
        const wrapper = createWrapper();
        const firstCanvas = await renderPageToCanvas(firstPage, availableWidth, maxWidth, pixelRatio);
        wrapper.appendChild(firstCanvas);

        if (cancelled || generation !== renderGenerationRef.current) return;

        mountWrapper(mountNode, wrapper);
        hasContentRef.current = true;
        lastWidthRef.current = availableWidth;
        contentHeightRef.current = wrapper.offsetHeight || estimateDefaultPreviewHeight(shellNode);
        setReservedHeight(Math.max(estimateDefaultPreviewHeight(shellNode), contentHeightRef.current));
        setError(null);
        setInitialLoading(false);
        if (typeof onReadyRef.current === 'function') {
          onReadyRef.current();
        }

        // Remaining pages append after first paint so the dialog feels ready sooner.
        for (let pageNum = 2; pageNum <= numPages; pageNum += 1) {
          if (cancelled || generation !== renderGenerationRef.current) return;
          const page = await pdf.getPage(pageNum);
          const canvas = await renderPageToCanvas(page, availableWidth, maxWidth, pixelRatio);
          canvas.style.marginTop = `${PAGE_GAP}px`;
          wrapper.appendChild(canvas);
          contentHeightRef.current = wrapper.offsetHeight;
          setReservedHeight(Math.max(estimateDefaultPreviewHeight(shellNode), contentHeightRef.current));
        }
      } catch (err) {
        if (cancelled || generation !== renderGenerationRef.current) return;
        if (!hasContentRef.current) {
          const message = err?.message || 'Failed to render PDF preview.';
          setError(message);
          setInitialLoading(false);
          if (typeof onErrorRef.current === 'function') {
            onErrorRef.current(message);
          }
        }
      }
    }

    renderPdf();

    let resizeTimer;
    const renderObserver = new ResizeObserver(() => {
      if (!hasContentRef.current) {
        syncReservedHeight();
        return;
      }
      const nextWidth = getAvailableWidth(shellNode);
      if (Math.abs(nextWidth - lastWidthRef.current) < WIDTH_EPSILON) return;

      window.clearTimeout(resizeTimer);
      resizeTimer = window.setTimeout(() => {
        if (!cancelled) renderPdf();
      }, 220);
    });
    renderObserver.observe(shellNode);

    return () => {
      cancelled = true;
      window.clearTimeout(resizeTimer);
      renderObserver.disconnect();
    };
  }, [pdfUrl, syncReservedHeight]);

  return (
    <Box
      ref={shellRef}
      sx={{
        position: 'relative',
        width: '100%',
        minHeight: reservedHeight,
        overflow: 'visible',
        bgcolor: '#e8edf3',
        borderRadius: 1,
        // Avoid animating min-height on first paint — that was causing layout "blips".
        transition: hasContentRef.current ? 'min-height 160ms ease' : 'none',
      }}
    >
      <Box ref={mountRef} sx={{ width: '100%', minHeight: reservedHeight }} />
      {initialLoading ? (
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexDirection: 'column',
            gap: 1.5,
            zIndex: 1,
            pointerEvents: 'none',
            bgcolor: '#e8edf3',
          }}
        >
          <Box
            sx={{
              width: 'min(320px, 72%)',
              aspectRatio: '210 / 297',
              borderRadius: '4px',
              bgcolor: '#ffffff',
              boxShadow: '0 12px 32px rgba(15, 23, 42, 0.12)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexDirection: 'column',
              gap: 1.25,
            }}
          >
            <CircularProgress size={28} sx={{ color: accentColor }} />
            <Typography variant="body2" color="text.secondary">
              Rendering preview…
            </Typography>
          </Box>
        </Box>
      ) : null}
      {error ? (
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            px: 3,
            zIndex: 1,
            bgcolor: '#e8edf3',
          }}
        >
          <Typography variant="body2" color="error" sx={{ textAlign: 'center' }}>
            {error}
          </Typography>
        </Box>
      ) : null}
    </Box>
  );
}
